// src/client.ts
var APIClient = class {
  baseUrl;
  timeout;
  headers;
  cache;
  authToken = null;
  constructor(config) {
    this.baseUrl = config.baseUrl.replace(/\/$/, "");
    this.timeout = config.timeout ?? 3e4;
    this.headers = {
      "Content-Type": "application/json",
      ...config.headers
    };
    this.cache = config.cache;
  }
  /**
   * Sets the authentication token for subsequent requests
   */
  setAuthToken(token) {
    this.authToken = token;
  }
  /**
   * Gets the current authentication token
   */
  getAuthToken() {
    return this.authToken;
  }
  /**
   * Builds headers for a request
   */
  buildHeaders() {
    const headers = { ...this.headers };
    if (this.authToken) {
      headers["Authorization"] = `Bearer ${this.authToken}`;
    }
    return headers;
  }
  /**
   * Makes a GET request
   */
  async get(path, params) {
    const url = new URL(`${this.baseUrl}${path}`);
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        url.searchParams.append(key, value);
      });
    }
    return this.request(url.toString(), {
      method: "GET"
    });
  }
  /**
   * Makes a POST request
   */
  async post(path, body) {
    return this.request(`${this.baseUrl}${path}`, {
      method: "POST",
      body: body ? JSON.stringify(body) : void 0
    });
  }
  /**
   * Makes a PUT request
   */
  async put(path, body) {
    return this.request(`${this.baseUrl}${path}`, {
      method: "PUT",
      body: body ? JSON.stringify(body) : void 0
    });
  }
  /**
   * Makes a PATCH request
   */
  async patch(path, body) {
    return this.request(`${this.baseUrl}${path}`, {
      method: "PATCH",
      body: body ? JSON.stringify(body) : void 0
    });
  }
  /**
   * Makes a DELETE request
   */
  async delete(path) {
    return this.request(`${this.baseUrl}${path}`, {
      method: "DELETE"
    });
  }
  /**
   * Uploads a file
   */
  async upload(path, file, metadata) {
    const formData = new FormData();
    formData.append("file", file);
    if (metadata) {
      Object.entries(metadata).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }
    const headers = this.buildHeaders();
    delete headers["Content-Type"];
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);
    try {
      const response = await fetch(`${this.baseUrl}${path}`, {
        method: "POST",
        headers,
        body: formData,
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      return this.handleResponse(response);
    } catch (error) {
      clearTimeout(timeoutId);
      return this.handleError(error);
    }
  }
  /**
   * Makes a request with timeout and error handling
   */
  async request(url, options) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);
    try {
      const fetchOptions = {
        ...options,
        headers: this.buildHeaders(),
        signal: controller.signal
      };
      if (this.cache !== void 0) {
        fetchOptions.cache = this.cache;
      }
      const response = await fetch(url, fetchOptions);
      clearTimeout(timeoutId);
      return this.handleResponse(response);
    } catch (error) {
      clearTimeout(timeoutId);
      return this.handleError(error);
    }
  }
  /**
   * Handles API response
   */
  async handleResponse(response) {
    if (response.ok) {
      if (response.status === 204) {
        return { success: true, data: void 0 };
      }
      const data = await response.json();
      return { success: true, data };
    }
    try {
      const errorData = await response.json();
      const rawError = errorData && (errorData.error ?? errorData) || {};
      return {
        success: false,
        error: {
          code: rawError.code ?? `E${response.status}`,
          message: rawError.message ?? response.statusText,
          details: rawError.details,
          // Preserve server-provided timestamp when possible
          timestamp: rawError.timestamp ? new Date(rawError.timestamp) : /* @__PURE__ */ new Date()
        }
      };
    } catch {
      return {
        success: false,
        error: {
          code: `E${response.status}`,
          message: response.statusText,
          timestamp: /* @__PURE__ */ new Date()
        }
      };
    }
  }
  /**
   * Handles request errors
   */
  handleError(error) {
    if (error instanceof Error) {
      if (error.name === "AbortError") {
        return {
          success: false,
          error: {
            code: "E408",
            message: "Request timeout",
            timestamp: /* @__PURE__ */ new Date()
          }
        };
      }
      return {
        success: false,
        error: {
          code: "E500",
          message: error.message,
          timestamp: /* @__PURE__ */ new Date()
        }
      };
    }
    return {
      success: false,
      error: {
        code: "E500",
        message: "Unknown error",
        timestamp: /* @__PURE__ */ new Date()
      }
    };
  }
};

// src/endpoints/outlets.ts
var OutletsAPI = class {
  constructor(client) {
    this.client = client;
  }
  /**
   * Get all outlets
   */
  async getAll() {
    return this.client.get("/api/outlets");
  }
  /**
   * Get outlet by ID
   */
  async getById(outletId) {
    return this.client.get(`/api/outlets/${outletId}`);
  }
  /**
   * Create a new outlet
   */
  async create(data) {
    return this.client.post("/api/outlets", data);
  }
  /**
   * Update an outlet
   */
  async update(outletId, data) {
    return this.client.patch(`/api/outlets/${outletId}`, data);
  }
  /**
   * Delete an outlet
   */
  async delete(outletId) {
    return this.client.delete(`/api/outlets/${outletId}`);
  }
  /**
   * Upload outlet photo (admin only)
   */
  async uploadPhoto(outletId, file) {
    return this.client.upload(`/api/outlets/${outletId}/photo`, file);
  }
  /**
   * Get outlet status
   */
  async getStatus(outletId) {
    return this.client.get(`/api/outlets/${outletId}/status`);
  }
  /**
   * Get all outlet statuses
   */
  async getAllStatuses() {
    return this.client.get("/api/outlets/status");
  }
  /**
   * Get active outlets for authentication (no auth required)
   */
  async getActiveOutlets() {
    return this.client.get("/api/outlets/public");
  }
  /**
   * Validate outlet PIN for authentication
   */
  async validatePin(outletId, pin, role) {
    const data = { pin, role };
    return this.client.post(`/api/outlets/${outletId}/validate-pin`, data);
  }
  /**
   * Update outlet PIN (admin only)
   * @deprecated Use updateRolePin instead for role-specific PIN management
   */
  async updatePin(outletId, pin) {
    return this.client.put(`/api/outlets/${outletId}/pin`, { pin });
  }
  /**
   * Update role-specific PIN (admin only)
   */
  async updateRolePin(outletId, role, pin) {
    return this.client.put(`/api/outlets/${outletId}/pin/${role}`, { pin });
  }
  /**
   * Get stock levels for outlet
   */
  async getStock(outletId) {
    return this.client.get(`/api/outlets/${outletId}/stock`);
  }
  /**
   * Update stock quantity for outlet
   */
  async updateStock(outletId, consumableTypeId, quantity) {
    return this.client.patch(`/api/outlets/${outletId}/stock/${consumableTypeId}`, { quantity });
  }
};

// src/endpoints/products.ts
var ProductsAPI = class {
  constructor(client) {
    this.client = client;
  }
  /**
   * Get all products
   */
  async getAll() {
    return this.client.get("/api/products");
  }
  /**
   * Get product by ID
   */
  async getById(productId) {
    return this.client.get(`/api/products/${productId}`);
  }
  /**
   * Get products by category
   */
  async getByCategory(categoryId) {
    return this.client.get("/api/products", { categoryId });
  }
  /**
   * Get products for an outlet (with outlet-specific pricing)
   */
  async getByOutlet(outletId) {
    return this.client.get("/api/products", {
      outletId,
      active: "true"
    });
  }
  /**
   * Create a new product
   */
  async create(data) {
    return this.client.post("/api/products", data);
  }
  /**
   * Update a product
   */
  async update(productId, data) {
    return this.client.patch(`/api/products/${productId}`, data);
  }
  /**
   * Delete a product
   */
  async delete(productId) {
    return this.client.delete(`/api/products/${productId}`);
  }
  /**
   * Upload product image (admin only)
   */
  async uploadPhoto(productId, file) {
    return this.client.upload(`/api/products/${productId}/photo`, file);
  }
  /**
   * Get price list for an outlet
   */
  async getPriceList(outletId, categoryId) {
    const params = {};
    if (outletId) params.outletId = outletId;
    if (categoryId) params.categoryId = categoryId;
    return this.client.get("/api/products/prices", params);
  }
  /**
   * Get all outlet pricing for a product
   */
  async getProductPricing(productId) {
    return this.client.get(
      `/api/products/${productId}/pricing`
    );
  }
  /**
   * Set outlet-specific pricing
   */
  async setOutletPricing(productId, outletId, data) {
    return this.client.put(
      `/api/products/${productId}/pricing/${outletId}`,
      data
    );
  }
  /**
   * Delete outlet-specific pricing
   */
  async deleteOutletPricing(productId, outletId) {
    return this.client.delete(`/api/products/${productId}/pricing/${outletId}`);
  }
};
var CategoriesAPI = class {
  constructor(client) {
    this.client = client;
  }
  /**
   * Get all categories
   */
  async getAll() {
    return this.client.get("/api/categories");
  }
  /**
   * Get category by ID
   */
  async getById(categoryId) {
    return this.client.get(`/api/categories/${categoryId}`);
  }
  /**
   * Create a new category
   */
  async create(data) {
    return this.client.post("/api/categories", data);
  }
  /**
   * Update a category
   */
  async update(categoryId, data) {
    return this.client.put(`/api/categories/${categoryId}`, data);
  }
  /**
   * Delete a category
   */
  async delete(categoryId) {
    return this.client.delete(`/api/categories/${categoryId}`);
  }
};

// src/endpoints/transactions.ts
var TransactionsAPI = class {
  constructor(client) {
    this.client = client;
  }
  /**
   * Create a new order/transaction
   */
  async createOrder(data) {
    return this.client.post("/api/transactions/orders", data);
  }
  /**
   * Approve cash payment (cashier)
   */
  async approvePayment(transactionId) {
    return this.client.patch(`/api/transactions/${transactionId}/payment`, {
      paymentStatus: "completed"
    });
  }
  /**
   * Cancel pending cash payment (cashier)
   */
  async cancelPayment(transactionId) {
    return this.client.patch(`/api/transactions/${transactionId}/payment`, {
      paymentStatus: "cancelled"
    });
  }
  /**
   * Get transaction by ID
   */
  async getById(transactionId) {
    return this.client.get(`/api/transactions/${transactionId}`);
  }
  /**
   * Validate a transaction ID (for capture terminal)
   */
  async validate(transactionId) {
    return this.client.get(
      `/api/transactions/${transactionId}/validate`
    );
  }
  /**
   * Initiate QRIS payment for a transaction
   */
  async initiatePayment(transactionId) {
    return this.client.post(`/api/transactions/${transactionId}/payment`);
  }
  /**
   * Check payment status
   */
  async getPaymentStatus(transactionId) {
    return this.client.get(`/api/transactions/${transactionId}/payment/status`);
  }
  /**
   * Update capture, edit, and/or print status (admin only)
   */
  async updateStatus(transactionId, data) {
    return this.client.patch(`/api/transactions/${transactionId}/status`, data);
  }
  /**
   * Update payment status (cashier/admin)
   */
  async updatePaymentStatus(transactionId, paymentStatus) {
    return this.client.patch(`/api/transactions/${transactionId}/payment`, {
      paymentStatus
    });
  }
  /**
   * Update capture status
   */
  async updateCaptureStatus(transactionId, status) {
    return this.client.patch(`/api/transactions/${transactionId}/capture`, { status });
  }
  /**
   * Update edit status
   */
  async updateEditStatus(transactionId, status) {
    return this.client.patch(`/api/transactions/${transactionId}/edit`, { status });
  }
  /**
   * Update print status
   */
  async updatePrintStatus(transactionId, status) {
    return this.client.patch(`/api/transactions/${transactionId}/print`, { status });
  }
  /**
   * List transactions with optional filters (admin)
   */
  async getList(params) {
    const queryParams = {};
    if (params?.outletId) queryParams.outletId = params.outletId;
    if (params?.startDate) queryParams.startDate = params.startDate;
    if (params?.endDate) queryParams.endDate = params.endDate;
    if (params?.search) queryParams.search = params.search;
    if (params?.paymentStatus) queryParams.paymentStatus = params.paymentStatus;
    if (params?.captureStatus) queryParams.captureStatus = params.captureStatus;
    if (params?.limit !== void 0) queryParams.limit = String(params.limit);
    if (params?.offset !== void 0) queryParams.offset = String(params.offset);
    return this.client.get("/api/transactions", queryParams);
  }
  /**
   * Add add-on products to an existing completed transaction
   */
  async addAddons(transactionId, items) {
    return this.client.post(`/api/transactions/${transactionId}/addons`, { items });
  }
  /**
   * Remove an add-on from a transaction
   */
  async removeAddon(transactionId, addonId) {
    return this.client.delete(`/api/transactions/${transactionId}/addons/${addonId}`);
  }
  /**
   * Get transactions for an outlet
   */
  async getByOutlet(outletId, params) {
    const queryParams = { outletId };
    if (params?.startDate) queryParams.startDate = params.startDate;
    if (params?.endDate) queryParams.endDate = params.endDate;
    if (params?.search) queryParams.search = params.search;
    if (params?.paymentStatus) queryParams.paymentStatus = params.paymentStatus;
    if (params?.captureStatus) queryParams.captureStatus = params.captureStatus;
    if (params?.editStatus) queryParams.editStatus = params.editStatus;
    if (params?.printStatus) queryParams.printStatus = params.printStatus;
    if (params?.limit !== void 0) queryParams.limit = String(params.limit);
    if (params?.offset !== void 0) queryParams.offset = String(params.offset);
    return this.client.get("/api/transactions", queryParams);
  }
};

// src/endpoints/frames.ts
var FramesAPI = class {
  constructor(client) {
    this.client = client;
  }
  /**
   * Get all frames
   */
  async getAll() {
    return this.client.get("/api/frames");
  }
  /**
   * Get frame by ID
   */
  async getById(frameId) {
    return this.client.get(`/api/frames/${frameId}`);
  }
  /**
   * Get frames by frame category
   */
  async getByCategory(frameCategoryId) {
    return this.client.get("/api/frames", { frameCategoryId });
  }
  /**
   * Get frames by paper type
   */
  async getByPaperType(paperType) {
    return this.client.get("/api/frames", { paperType });
  }
  /**
   * Upload a new frame
   */
  async upload(file, metadata) {
    return this.client.upload("/api/frames", file, {
      name: metadata.name,
      frameCategoryId: metadata.frameCategoryId,
      paperType: metadata.paperType
    });
  }
  /**
   * Update frame metadata
   */
  async update(frameId, data) {
    return this.client.patch(`/api/frames/${frameId}`, data);
  }
  /**
   * Delete a frame
   */
  async delete(frameId) {
    return this.client.delete(`/api/frames/${frameId}`);
  }
  /**
   * Assign frame to frame category
   */
  async assignToCategory(frameId, frameCategoryId) {
    return this.client.patch(`/api/frames/${frameId}`, { frameCategoryId });
  }
};

// src/endpoints/frame-categories.ts
var FrameCategoriesAPI = class {
  constructor(client) {
    this.client = client;
  }
  /**
   * Get all frame categories
   */
  async getAll() {
    return this.client.get("/api/frame-categories");
  }
  /**
   * Get frame category by ID
   */
  async getById(frameCategoryId) {
    return this.client.get(`/api/frame-categories/${frameCategoryId}`);
  }
  /**
   * Create a new frame category
   */
  async create(data) {
    return this.client.post("/api/frame-categories", data);
  }
  /**
   * Update a frame category
   */
  async update(frameCategoryId, data) {
    return this.client.put(`/api/frame-categories/${frameCategoryId}`, data);
  }
  /**
   * Delete a frame category
   */
  async delete(frameCategoryId) {
    return this.client.delete(`/api/frame-categories/${frameCategoryId}`);
  }
};

// src/endpoints/users.ts
var UsersAPI = class {
  constructor(client) {
    this.client = client;
  }
  /**
   * Get all users
   */
  async getAll() {
    return this.client.get("/api/users");
  }
  /**
   * Get user by ID
   */
  async getById(userId) {
    return this.client.get(`/api/users/${userId}`);
  }
  /**
   * Create a new user
   */
  async create(data) {
    return this.client.post("/api/users", data);
  }
  /**
   * Update a user
   */
  async update(userId, data) {
    return this.client.patch(`/api/users/${userId}`, data);
  }
  /**
   * Deactivate a user
   */
  async deactivate(userId) {
    return this.client.patch(`/api/users/${userId}`, { isActive: false });
  }
  /**
   * Delete a user
   */
  async delete(userId) {
    return this.client.delete(`/api/users/${userId}`);
  }
};
var AuthAPI = class {
  constructor(client) {
    this.client = client;
  }
  /**
   * Login with username and password (Admin)
   */
  async login(data) {
    const response = await this.client.post("/api/auth/login", data);
    if (response.success && response.data) {
      this.client.setAuthToken(response.data.token);
    }
    return response;
  }
  /**
   * Logout (Admin)
   */
  async logout() {
    const response = await this.client.post("/api/auth/logout");
    this.client.setAuthToken(null);
    return response;
  }
  /**
   * Get current user (Admin)
   */
  async getCurrentUser() {
    return this.client.get("/api/auth/me");
  }
  /**
   * Refresh token (Admin)
   */
  async refreshToken() {
    const response = await this.client.post("/api/auth/refresh");
    if (response.success && response.data) {
      this.client.setAuthToken(response.data.token);
    }
    return response;
  }
  /**
   * Cashier login with username and password
   */
  async cashierLogin(data) {
    return this.client.post("/api/auth/cashier/login", data);
  }
  /**
   * Cashier logout
   */
  async cashierLogout() {
    return this.client.post("/api/auth/cashier/logout", {});
  }
};

// src/endpoints/cashiers.ts
var CashiersAPI = class {
  constructor(client) {
    this.client = client;
  }
  /**
   * Get all cashiers
   */
  async getAll(outletId) {
    const params = outletId ? `?outletId=${outletId}` : "";
    return this.client.get(`/api/users/cashiers${params}`);
  }
  /**
   * Get cashier by ID
   */
  async getById(userId) {
    return this.client.get(`/api/users/cashiers/${userId}`);
  }
  /**
   * Create a new cashier
   */
  async create(data) {
    return this.client.post("/api/users/cashiers", data);
  }
  /**
   * Update a cashier
   */
  async update(userId, data) {
    return this.client.patch(`/api/users/cashiers/${userId}`, data);
  }
  /**
   * Delete a cashier
   */
  async delete(userId) {
    return this.client.delete(`/api/users/cashiers/${userId}`);
  }
  /**
   * Manage outlet access for a cashier
   */
  async manageOutletAccess(userId, data) {
    return this.client.post(`/api/users/cashiers/${userId}/outlets`, data);
  }
  /**
   * Get outlets a cashier has access to
   */
  async getOutlets(userId) {
    return this.client.get(`/api/users/cashiers/${userId}/outlets`);
  }
  /**
   * Get audit logs for a cashier
   */
  async getAuditLogs(userId, params) {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append("startDate", params.startDate);
    if (params?.endDate) queryParams.append("endDate", params.endDate);
    if (params?.outletId) queryParams.append("outletId", params.outletId);
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.offset) queryParams.append("offset", params.offset.toString());
    const query = queryParams.toString();
    const url = `/api/users/cashiers/${userId}/audit-logs${query ? `?${query}` : ""}`;
    return this.client.get(url);
  }
};

// src/endpoints/photos.ts
var PhotosAPI = class {
  constructor(client) {
    this.client = client;
  }
  /**
   * Get photos by transaction ID
   */
  async getByTransaction(transactionId) {
    return this.client.get(`/api/transactions/${transactionId}/photos`);
  }
  /**
   * Get photo by ID
   */
  async getById(photoId) {
    return this.client.get(`/api/photos/${photoId}`);
  }
  /**
   * Upload a captured photo
   */
  async upload(transactionId, file, sequence) {
    return this.client.upload(`/api/transactions/${transactionId}/photos`, file, {
      sequence: String(sequence)
    });
  }
  /**
   * Update photo (apply frame, edits)
   */
  async update(photoId, data) {
    return this.client.patch(`/api/photos/${photoId}`, data);
  }
  /**
   * Get photos for download (public website)
   */
  async getForDownload(transactionId) {
    return this.client.get(`/api/public/photos/${transactionId}`);
  }
};

// src/endpoints/reports.ts
var ReportsAPI = class {
  constructor(client) {
    this.client = client;
  }
  /**
   * Get revenue report with filters
   */
  async getRevenueReport(filters) {
    const params = {};
    if (filters.startDate) params.startDate = filters.startDate.toISOString();
    if (filters.endDate) params.endDate = filters.endDate.toISOString();
    if (filters.outletId) params.outletId = filters.outletId;
    if (filters.productId) params.productId = filters.productId;
    if (filters.categoryId) params.categoryId = filters.categoryId;
    return this.client.get("/api/reports/revenue", params);
  }
  /**
   * Get outlet comparison report
   */
  async getOutletComparison(startDate, endDate, outletIds) {
    const params = {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    };
    if (outletIds) params.outletIds = outletIds.join(",");
    return this.client.get("/api/reports/outlets/comparison", params);
  }
  /**
   * Export revenue report as CSV
   */
  async exportCSV(filters) {
    const params = { format: "csv" };
    if (filters.startDate) params.startDate = filters.startDate.toISOString();
    if (filters.endDate) params.endDate = filters.endDate.toISOString();
    if (filters.outletId) params.outletId = filters.outletId;
    if (filters.productId) params.productId = filters.productId;
    const url = new URL("/api/reports/revenue/export", "http://placeholder");
    Object.entries(params).forEach(([key, value]) => url.searchParams.append(key, value));
    return new Blob();
  }
  /**
   * Export revenue report as PDF
   */
  async exportPDF(filters) {
    const params = { format: "pdf" };
    if (filters.startDate) params.startDate = filters.startDate.toISOString();
    if (filters.endDate) params.endDate = filters.endDate.toISOString();
    if (filters.outletId) params.outletId = filters.outletId;
    if (filters.productId) params.productId = filters.productId;
    return new Blob();
  }
};

// src/endpoints/branding.ts
var BrandingAPI = class {
  constructor(client) {
    this.client = client;
  }
  /**
   * Get global branding configuration
   */
  async getBranding() {
    const response = await this.client.get("/api/branding");
    if (!response.success) {
      throw new Error(response.error?.message || "Failed to fetch branding");
    }
    return response.data;
  }
  /**
   * Update global branding configuration (admin only)
   */
  async updateBranding(updates) {
    const response = await this.client.put("/api/branding", updates);
    if (!response.success) {
      throw new Error(response.error?.message || "Failed to update branding");
    }
    return response.data;
  }
  /**
   * Reset branding to defaults (admin only)
   */
  async resetBranding() {
    const response = await this.client.post("/api/branding/reset");
    if (!response.success) {
      throw new Error(response.error?.message || "Failed to reset branding");
    }
    return response.data;
  }
  /**
   * Upload primary logo (admin only)
   */
  async uploadLogo(file) {
    const response = await this.client.upload("/api/branding/assets/logo", file);
    if (!response.success) {
      throw new Error(response.error?.message || "Failed to upload logo");
    }
    return response.data;
  }
  /**
   * Upload secondary logo (admin only)
   */
  async uploadLogoSecondary(file) {
    const response = await this.client.upload("/api/branding/assets/logo-secondary", file);
    if (!response.success) {
      throw new Error(response.error?.message || "Failed to upload secondary logo");
    }
    return response.data;
  }
  /**
   * Upload watermark (admin only)
   */
  async uploadWatermark(file) {
    const response = await this.client.upload("/api/branding/assets/watermark", file);
    if (!response.success) {
      throw new Error(response.error?.message || "Failed to upload watermark");
    }
    return response.data;
  }
  /**
   * Upload background image (admin only)
   */
  async uploadBackground(file) {
    const response = await this.client.upload("/api/branding/assets/background", file);
    if (!response.success) {
      throw new Error(response.error?.message || "Failed to upload background");
    }
    return response.data;
  }
};

// src/endpoints/config.ts
var ConfigAPI = class {
  constructor(client) {
    this.client = client;
  }
  /**
   * Get global capture configuration
   */
  async getCaptureConfig() {
    const response = await this.client.get("/api/config/capture");
    if (!response.success) {
      throw new Error(response.error?.message || "Failed to fetch capture config");
    }
    return response.data;
  }
  /**
   * Update global capture configuration (admin only)
   */
  async updateCaptureConfig(config) {
    const response = await this.client.put("/api/config/capture", config);
    if (!response.success) {
      throw new Error(response.error?.message || "Failed to update capture config");
    }
    return response.data;
  }
};

// src/endpoints/vouchers.ts
var VouchersAPI = class {
  constructor(client) {
    this.client = client;
  }
  /**
   * List vouchers with optional filters
   */
  async getAll(params) {
    const queryParams = {};
    if (params?.isActive !== void 0) queryParams.isActive = String(params.isActive);
    if (params?.search) queryParams.search = params.search;
    return this.client.get("/api/vouchers", queryParams);
  }
  /**
   * Get voucher by ID
   */
  async getById(id) {
    return this.client.get(`/api/vouchers/${id}`);
  }
  /**
   * Validate voucher for a product/outlet
   */
  async validate(data) {
    return this.client.post("/api/vouchers/validate", data);
  }
  /**
   * Create voucher
   */
  async create(data) {
    return this.client.post("/api/vouchers", data);
  }
  /**
   * Update voucher
   */
  async update(id, data) {
    return this.client.patch(`/api/vouchers/${id}`, data);
  }
  /**
   * Delete (deactivate) voucher
   */
  async delete(id) {
    return this.client.delete(`/api/vouchers/${id}`);
  }
};

// src/index.ts
var PhotoBoothAPI = class {
  client;
  outlets;
  products;
  categories;
  transactions;
  frames;
  frameCategories;
  users;
  auth;
  cashiers;
  photos;
  reports;
  branding;
  config;
  vouchers;
  constructor(config) {
    this.client = new APIClient(config);
    this.outlets = new OutletsAPI(this.client);
    this.products = new ProductsAPI(this.client);
    this.categories = new CategoriesAPI(this.client);
    this.transactions = new TransactionsAPI(this.client);
    this.frames = new FramesAPI(this.client);
    this.frameCategories = new FrameCategoriesAPI(this.client);
    this.users = new UsersAPI(this.client);
    this.auth = new AuthAPI(this.client);
    this.cashiers = new CashiersAPI(this.client);
    this.photos = new PhotosAPI(this.client);
    this.reports = new ReportsAPI(this.client);
    this.branding = new BrandingAPI(this.client);
    this.config = new ConfigAPI(this.client);
    this.vouchers = new VouchersAPI(this.client);
  }
  /**
   * Set authentication token
   */
  setAuthToken(token) {
    this.client.setAuthToken(token);
  }
  /**
   * Get current authentication token
   */
  getAuthToken() {
    return this.client.getAuthToken();
  }
};
function createPhotoBoothAPI(config) {
  return new PhotoBoothAPI(config);
}
export {
  APIClient,
  AuthAPI,
  BrandingAPI,
  CashiersAPI,
  CategoriesAPI,
  ConfigAPI,
  FrameCategoriesAPI,
  FramesAPI,
  OutletsAPI,
  PhotoBoothAPI,
  PhotosAPI,
  ProductsAPI,
  ReportsAPI,
  TransactionsAPI,
  UsersAPI,
  VouchersAPI,
  createPhotoBoothAPI
};
